/**
 * Roguelite Zone Generator — wraps ascent terrain generation with
 * checkpoint platforms and enemy spawn positions.
 */

import { ROGUELITE } from '../constants';
import { generateTerrain } from '../terrain/generation';
import { getAllTankIds } from '../tanks';
import { SeededRNG } from '../terrain/rng';

export interface EnemySpawn {
  x: number;
  y: number;
  tankId: string;
  aiDifficulty: number;
  zone: number;
}

export interface RogueliteTerrainResult {
  seed: number;
  biomeDisplayName: string;
  featureDisplayName: string | null;
  spawnXs: number[];
  stageLeft: number;
  stageRight: number;
  checkpointYs: number[];
  enemySpawns: EnemySpawn[];
}

/** Generate roguelite terrain: ascent base + checkpoint platforms + enemy spawns. */
export function generateRogueliteTerrain(
  playerTankId: string,
  seed?: number,
  humanCount: number = 1,
): RogueliteTerrainResult {
  // Generate base ascent terrain (humanCount players; enemies added after)
  const base = generateTerrain(humanCount, seed, 'ascent');

  // Compute checkpoint Ys from zone boundaries (Phase 1: zones 1-4)
  const zoneCount = ROGUELITE.ZONE_ENEMIES.length;
  const checkpointYs = ROGUELITE.ZONE_BOUNDARIES.slice(0, zoneCount);

  // RNG for enemy placement (deterministic from terrain seed)
  const rng = new SeededRNG((base.seed ^ 0xBEEF) >>> 0);

  // Pick valid tank IDs for enemies (exclude player's tank for variety)
  const allTanks = getAllTankIds();
  const enemyTanks = allTanks.filter(id => id !== playerTankId);
  if (enemyTanks.length === 0) enemyTanks.push(allTanks[0]); // fallback

  const enemySpawns: EnemySpawn[] = [];
  const stageCenter = (base.stageLeft + base.stageRight) / 2;
  const stageHalfWidth = (base.stageRight - base.stageLeft) / 2;

  for (let z = 0; z < zoneCount; z++) {
    const enemyCount = ROGUELITE.ZONE_ENEMIES[z];
    const zoneY = checkpointYs[z];
    const difficulty = ROGUELITE.ZONE_AI_DIFFICULTY[z];

    for (let e = 0; e < enemyCount; e++) {
      // Spread enemies horizontally around stage center within zone
      const xOffset = (rng.next() - 0.5) * stageHalfWidth * 1.2;
      const x = Math.round(stageCenter + xOffset);
      // Place enemies slightly above the zone boundary (on platforms)
      const yOffset = 100 + rng.next() * 300;
      const y = zoneY - yOffset;

      const tankId = enemyTanks[Math.floor(rng.next() * enemyTanks.length)];

      enemySpawns.push({
        x: Math.max(base.stageLeft + 50, Math.min(base.stageRight - 50, x)),
        y: Math.max(200, y),
        tankId,
        aiDifficulty: difficulty,
        zone: z + 1,
      });
    }
  }

  return {
    seed: base.seed,
    biomeDisplayName: base.biomeDisplayName,
    featureDisplayName: base.featureDisplayName,
    spawnXs: base.spawnXs,
    stageLeft: base.stageLeft,
    stageRight: base.stageRight,
    checkpointYs,
    enemySpawns,
  };
}
